import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import { addMonths, format, parse, startOfMonth, subMonths } from 'date-fns';
import { BreadcrumbItem } from 'src/components/UI/TabTemplate';
import { IndicatorAlertConfiguration } from 'src/types/indicator-alert-configuration';
import { Wallet } from 'src/types/wallet';
import { debounce } from 'src/utils/general';

import { CSVValidationResult } from './validation';

const CURRENT_MONTH_MINUS_ONE_MONTH = subMonths(new Date(), 1);

const DEFAULT_DATE = format(
  startOfMonth(CURRENT_MONTH_MINUS_ONE_MONTH),
  'yyyy-MM-dd',
);

export type WalletDraft = {
  name: string;
  description: string;
  startDate: Date | null;
  funds?: {
    operationId: number;
    quotas?: {
      name: string;
      series: string;
      value: number;
      amount: number;
    }[];
  }[];
};

export type MonitorUploadState = {
  document: File | null;
  setDocument: (file: File | null) => void;
  csvValidation: CSVValidationResult | null;
  setCsvValidation: (validation: CSVValidationResult | null) => void;
};

type MonitorContextType = {
  funds: CSVValidationResult['data'];
  handleSetFunds: (funds: CSVValidationResult['data']) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  walletDraft: WalletDraft;
  setWalletDraft: (draft: WalletDraft) => void;
  document: File | null;
  setDocument: (file: File | null) => void;
  csvValidation: CSVValidationResult | null;
  setCsvValidation: (validation: CSVValidationResult | null) => void;
  wallet: Wallet | null;
  setWallet: Dispatch<SetStateAction<Wallet | null>>;
  breadcrumbsItens: BreadcrumbItem[];
  setBreadcrumbsItens: Dispatch<SetStateAction<BreadcrumbItem[]>>;
  indicatorsCompetencyDate: string;
  setIndicatorsCompetencyDate: Dispatch<SetStateAction<string>>;
  handleIndicatorsCompetencyDateChange: (
    e: React.MouseEvent<HTMLButtonElement>,
    direction: 'previous' | 'next',
  ) => void;
  defaultUserAlertConfigurations: IndicatorAlertConfiguration[];
  setDefaultUserAlertConfigurations: Dispatch<
    SetStateAction<IndicatorAlertConfiguration[]>
  >;
  globalFundsCount: number;
  setGlobalFundsCount: Dispatch<SetStateAction<number>>;
  guestEnvironmentOwnerId: string | null;
  setGuestEnvironmentOwnerId: Dispatch<SetStateAction<string | null>>;
};

const initialContext: MonitorContextType = {
  funds: [],
  handleSetFunds: () => {},
  isLoading: false,
  setIsLoading: () => {},
  walletDraft: {
    name: '',
    description: '',
    startDate: null,
  },
  setWalletDraft: () => {},

  document: null,
  setDocument: () => {},
  csvValidation: null,
  setCsvValidation: () => {},
  wallet: null,
  setWallet: () => {},
  breadcrumbsItens: [],
  setBreadcrumbsItens: () => {},
  indicatorsCompetencyDate: DEFAULT_DATE,
  setIndicatorsCompetencyDate: () => {},
  handleIndicatorsCompetencyDateChange: () => {},
  defaultUserAlertConfigurations: [],
  setDefaultUserAlertConfigurations: () => {},
  globalFundsCount: 0,
  setGlobalFundsCount: () => {},
  guestEnvironmentOwnerId: null,
  setGuestEnvironmentOwnerId: () => {},
};

const MonitorContext = createContext<MonitorContextType>(initialContext);

export function MonitorContextProvider({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(false);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [funds, setFunds] = useState<CSVValidationResult['data']>([]);
  const [defaultUserAlertConfigurations, setDefaultUserAlertConfigurations] =
    useState<IndicatorAlertConfiguration[]>([]);
  const [document, setDocument] = useState<File | null>(null);
  const [csvValidation, setCsvValidation] =
    useState<CSVValidationResult | null>(null);
  const [globalFundsCount, setGlobalFundsCount] = useState(0);
  const [guestEnvironmentOwnerId, setGuestEnvironmentOwnerId] = useState<
    string | null
  >(null);

  const [breadcrumbsItens, setBreadcrumbsItens] = useState<BreadcrumbItem[]>([
    'Monitor',
    { title: 'Dashboard de Carteiras', link: '/monitor' },
  ]);

  const [walletDraft, setWalletDraft] = useState<WalletDraft>({
    name: '',
    description: '',
    startDate: null,
    funds: [],
  });

  const handleSetFunds = (funds: CSVValidationResult['data']) => {
    setFunds(funds);
  };

  const [indicatorsCompetencyDate, setIndicatorsCompetencyDate] =
    useState(DEFAULT_DATE);

  const debouncedSetIndicatorsCompetencyDate = useCallback(
    debounce((value: string) => {
      setIndicatorsCompetencyDate(value);
    }, 500),
    [],
  );

  const handleIndicatorsCompetencyDateChange = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement>,
      direction: 'previous' | 'next',
    ) => {
      e.preventDefault();
      e.stopPropagation();

      if (direction === 'previous') {
        const previousDate = subMonths(
          parse(indicatorsCompetencyDate, 'yyyy-MM-dd', new Date()),
          1,
        );
        debouncedSetIndicatorsCompetencyDate(
          format(startOfMonth(previousDate), 'yyyy-MM-dd'),
        );
      } else {
        const currentDate = parse(
          indicatorsCompetencyDate,
          'yyyy-MM-dd',
          new Date(),
        );
        if (currentDate < CURRENT_MONTH_MINUS_ONE_MONTH) {
          const nextDate = addMonths(currentDate, 1);
          debouncedSetIndicatorsCompetencyDate(
            format(startOfMonth(nextDate), 'yyyy-MM-dd'),
          );
        }
      }
    },
    [indicatorsCompetencyDate],
  );

  return (
    <MonitorContext.Provider
      value={{
        funds,
        handleSetFunds,
        isLoading,
        setIsLoading,
        walletDraft,
        setWalletDraft,
        document,
        setDocument,
        csvValidation,
        setCsvValidation,
        wallet,
        setWallet,
        breadcrumbsItens,
        setBreadcrumbsItens,
        indicatorsCompetencyDate,
        setIndicatorsCompetencyDate,
        handleIndicatorsCompetencyDateChange,
        defaultUserAlertConfigurations,
        setDefaultUserAlertConfigurations,
        globalFundsCount,
        setGlobalFundsCount,
        guestEnvironmentOwnerId,
        setGuestEnvironmentOwnerId,
      }}>
      {children}
    </MonitorContext.Provider>
  );
}

export function useMonitorContext() {
  const context = useContext(MonitorContext);
  if (!context) {
    throw new Error(
      'useMonitorContext must be used within an MonitorContextProvider',
    );
  }
  return context;
}
